import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, Download, Phone, Loader2, X, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ServiceButtons } from "@/components/ServiceButtons";

const inputCls = "rounded-xl border border-neutral-700 bg-black px-4 py-3 text-sm text-stone-100 outline-none focus:border-white";

function ProductCheckoutModal({ product, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/products/checkout", { ...form, product_id: product.id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/80 p-4 backdrop-blur-sm sm:items-center" onClick={onClose} data-testid="product-checkout-modal">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="glass w-full max-w-lg rounded-3xl p-6 md:p-8">
        <div className="flex items-start justify-between">
          <div>
            <span className="eyebrow">{product.tag}</span>
            <h2 className="mt-1 font-display text-3xl uppercase text-chrome">{product.name}</h2>
          </div>
          <button type="button" onClick={onClose} data-testid="product-checkout-close" className="rounded-full p-2 text-neutral-400 hover:bg-white/10"><X className="h-5 w-5" /></button>
        </div>
        <div className="mt-4 flex items-center justify-between rounded-2xl border border-white/15 bg-black/30 p-4">
          <span className="text-sm text-neutral-300">{product.kind === "download" ? "Instant PDF download after payment" : "Engagement brief + scheduling within 1 business day"}</span>
          <span className="font-display text-2xl text-white" data-testid="product-price">${product.price.toLocaleString()}</span>
        </div>
        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          <input required placeholder="Full name" value={form.name} onChange={set("name")} data-testid="product-name" className={inputCls} />
          <input required type="email" placeholder="Email for delivery" value={form.email} onChange={set("email")} data-testid="product-email" className={inputCls} />
          <input placeholder="Company (optional)" value={form.company} onChange={set("company")} data-testid="product-company" className={inputCls} />
          <input placeholder="Phone (optional)" value={form.phone} onChange={set("phone")} data-testid="product-phone" className={inputCls} />
        </div>
        <button type="submit" disabled={busy} data-testid="product-checkout-pay" className="btn-gold mt-5 flex w-full items-center justify-center gap-2 rounded-full py-4 font-display text-lg uppercase disabled:opacity-60">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Lock className="h-4 w-4" /> Pay ${product.price.toLocaleString()}</>}
        </button>
        <p className="mt-3 text-center font-mono-label text-[10px] uppercase tracking-widest text-stone-600">Secure checkout by Stripe · Single-business license</p>
      </form>
    </div>
  );
}

const ProductCard = ({ p, i, onBuy }) => (
  <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
    className="glass flex flex-col rounded-3xl p-6 md:p-7" data-testid={`product-card-${p.id}`}>
    <div className="flex items-center gap-2 font-mono-label text-[10px] uppercase tracking-widest text-neutral-400">
      {p.kind === "download" ? <Download className="h-4 w-4" /> : <Phone className="h-4 w-4" />} {p.tag}
    </div>
    <h3 className="mt-3 font-cond text-2xl font-extrabold uppercase leading-tight text-white">{p.name}</h3>
    <div className="mt-2 font-display text-3xl text-white">${p.price.toLocaleString()}{p.id === "management-retainer" && <span className="text-base text-neutral-400"> / mo</span>}</div>
    <p className="mt-3 text-sm text-neutral-400">{p.pitch}</p>
    <ul className="mt-4 flex-1 space-y-1.5">{p.includes.map((d) => <li key={d} className="flex gap-2 text-sm text-neutral-300"><Check className="mt-0.5 h-4 w-4 shrink-0 text-white" />{d}</li>)}</ul>
    <button onClick={() => onBuy(p)} data-testid={`product-buy-${p.id}`} className="btn-chrome mt-6 w-full rounded-full py-3 text-[12px]">
      {p.kind === "download" ? "Buy & Download" : "Book Engagement"}
    </button>
  </motion.div>
);

export default function ProductsPage() {
  const [items, setItems] = useState([]);
  const [buying, setBuying] = useState(null);
  useEffect(() => {
    api.get("/products").then((r) => setItems(r.data)).catch(() => {});
    if (new URLSearchParams(window.location.search).get("cancelled")) toast("Checkout cancelled — your cart is still here.");
  }, []);
  const mgmt = items.filter((p) => p.category === "Management");
  return (
    <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 md:py-20" data-testid="products-page">
      <span className="eyebrow">Services & Digital Products · Management & Consulting</span>
      <h1 className="mt-2 font-display text-4xl uppercase text-chrome sm:text-5xl lg:text-6xl">Run Events <span className="text-gold">Like EON.</span></h1>
      <p className="mt-4 max-w-2xl text-neutral-300">Fixed-price consulting services you can buy directly, plus the systems we use to produce trail rides, concerts and corporate events as instant PDF downloads.</p>

      <div className="mt-14"><ServiceButtons /></div>

      <h2 className="mt-16 font-display text-2xl uppercase text-white" data-testid="products-management-heading">Management Systems · PDF</h2>
      <div className="mt-6 grid gap-5 md:grid-cols-2 lg:grid-cols-4">{mgmt.map((p, i) => <ProductCard key={p.id} p={p} i={i} onBuy={setBuying} />)}</div>

      <p className="mt-12 text-center text-sm text-neutral-500">Need a full production instead? <a href="/request" className="text-white underline">Start a request</a> — proposals in one business day.</p>
      {buying && <ProductCheckoutModal product={buying} onClose={() => setBuying(null)} />}
    </div>
  );
}
